import slugify from 'slugify';
import { HeaderSearch, Item, ProductCompact, ProductComponents } from 'ui/components';
import { ComboButton } from 'ui/components/Products';

import { HeaderSearchResultProps } from './HeaderSearchResult';
import HeaderSearchResultEmpty from './HeaderSearchResultEmpty';
import HeaderSkeletonSearch from './HeaderSkeletonSearch';
import { useHeaderSearchResultWidget } from './hooks/useHeaderSearchResultWidget';

const HeaderSearchResultWidget = ({ focus, query, handleClearSearch }: HeaderSearchResultProps) => {
  const {
    data,
    isLoading,
    notFound,
    addItem,
    updateItem,
    isBuyIntentionFeatureActive,
    setBuyIntention,
    isBuyIntention,
    isResaleIntention,
    isAutoStartEnabled,
    clickProductDetail,
  } = useHeaderSearchResultWidget({ handleClearSearch });

  if (isLoading) {
    return <HeaderSkeletonSearch focus={focus} />;
  }

  return notFound ? (
    <HeaderSearchResultEmpty focus={focus} query={query} />
  ) : (
    <HeaderSearch.List counter={false} data-visible={focus} rounded>
      {data.map((item) => {
        const isAvailableItem = item.isCustomCombo ? true : item.isAvailable;
        const slugNameStr = item.slugName ?? slugify(item.name);
        return (
          <Item key={item.productCode}>
            <ProductCompact.Component>
              <ProductCompact.Picture
                alt={item.name}
                opacity={isAvailableItem ? '100' : '25'}
                ratio="4x3"
                tabIndex={0}
                sources={[
                  {
                    srcSet: `${item.image}`,
                  },
                ]}
                src={item.image}
                onClick={() => clickProductDetail(slugNameStr, item.productCode)}
                data-cursor={true}
              />

              <ProductCompact.Info.Component
                onClick={() => clickProductDetail(slugNameStr, item.productCode)}
                data-cursor={true}
              >
                <ProductCompact.Info.Code
                  tabIndex={0}
                  aria-label={`o sku do produto é ${item.productCode}`}
                >
                  {item.productCode}
                </ProductCompact.Info.Code>
                <ProductCompact.Info.Title
                  tabIndex={0}
                  aria-label={`${item.name} clique para ver mais detalhes`}
                >
                  {item.name}
                </ProductCompact.Info.Title>
              </ProductCompact.Info.Component>

              <ProductCompact.PriceSmall.Component
                onClick={() => clickProductDetail(slugNameStr, item.productCode)}
                data-cursor={true}
              >
                <ProductCompact.PriceSmall.Title>Valor do Item</ProductCompact.PriceSmall.Title>
                <ProductCompact.PriceSmall.Price
                  tabIndex={0}
                  aria-label={`valor do item é ${item.price.price}`}
                >
                  {item.price.price}
                </ProductCompact.PriceSmall.Price>
              </ProductCompact.PriceSmall.Component>

              {item.isCustomCombo && isAvailableItem && (
                <ComboButton
                  name={item.name}
                  onClick={handleClearSearch}
                  productCode={item.productCode}
                />
              )}
              {!item.isCustomCombo && isAvailableItem && (
                <ProductComponents.AvailableButton
                  onAddProduct={addItem}
                  onChangeAmount={updateItem}
                  productCode={item.productCode}
                  productOrigin={item.productOrigin}
                  productPosition={item.productPosition}
                  quantity={item.quantity}
                  itemListName={item.itemListName}
                  name={item.name}
                  disabled={isResaleIntention && !isAutoStartEnabled}
                />
              )}
              {!isAvailableItem && (
                <ProductComponents.UnavailableButton
                  isBuyIntention={isBuyIntention(item.productCode)}
                  isBuyIntentionFeatureActive={isBuyIntentionFeatureActive && !item.isCustomCombo}
                  name={item.name}
                  productCode={item.productCode}
                  setBuyIntention={setBuyIntention}
                  isResaleIntention={isResaleIntention}
                  isAutoStartEnabled={isAutoStartEnabled}
                  slugName={slugNameStr}
                  hasSimilar={item.hasSimilar}
                  onClick={handleClearSearch}
                  tracking={{ origin: 'busca' }}
                />
              )}
            </ProductCompact.Component>
          </Item>
        );
      })}
    </HeaderSearch.List>
  );
};

export default HeaderSearchResultWidget;
